/**
 * Feedback validation utilities (Issue #1739)
 *
 * Validation functions for feedback-related operations.
 */

import { FeedbackType } from '@prisma/client-cadence'

/**
 * Whether a string is one of the real feedback types.
 *
 * The draft layer persists the selected type as a plain string inside
 * `form_drafts.draftData.meta`, so what comes back on restore is arbitrary: a
 * legacy value, a hand-edited row, or a shape written by an older build.
 * Casting it straight to the enum let `canSubmit` go true for a value
 * `validateFeedback` then rejects server-side, so the submitter met a failure
 * on submit rather than an unselected type they could see and fix (#13645).
 *
 * `Object.values` over the enum rather than a hand-kept literal union: a fifth
 * type added to the schema is accepted here without a second edit, and one
 * removed from it stops being accepted without one.
 */
export function isFeedbackType(value: string): value is FeedbackType {
  return (Object.values(FeedbackType) as string[]).includes(value)
}

/**
 * URL pattern for valid GitHub issue URLs in this repository.
 * Must match: https://github.com/SchoolAI/cadence/issues/123
 */
const GITHUB_ISSUE_URL_PATTERN =
  /^https:\/\/github\.com\/SchoolAI\/cadence\/issues\/\d+$/

/**
 * Validate that a URL is a valid GitHub issue URL for this repository.
 */
export function isValidGitHubIssueUrl(url: string): boolean {
  return GITHUB_ISSUE_URL_PATTERN.test(url)
}
